import { ContentTreeNode } from '@cube3/common/model/schema/resources/content-tree-node';
import { useCurrentUser } from '@cube3/state/src/redux/components/Administration/withCurrentUserAccount';
import { useContextPath } from '@cube3/state/src/redux/components/Hooks/useContextPath';
import { useResource__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResource';
import { mergeStatuses } from '@cube3/state/src/redux/utils/mergeStatuses';
import { generatePath } from 'react-router';
import {
  urlStructureContractView,
  urlStructureShareRedirectSettings
} from '../../../routing/routingPaths';
import { useCurrentWorkspace } from '@cube3/state/src/redux/components/Administration/withCurrentWorkspace';
import { getData } from '../utils/getRelationshipData';

export const useActivitySubject = (subjectRID, actor: string) => {
  const isNode = subjectRID.type === 'content-tree-node';
  const isShare = subjectRID?.type === 'share';
  const isProject = subjectRID?.type === 'project';
  const isContract = subjectRID?.type === 'contract';

  const activitySubject = useResource__ALPHA({
    resourceId: subjectRID?.id,
    resourceType: subjectRID?.type,
    // NOTE: the contract resource from `included` is missing neccessary attributes
    strategy: isContract ? 'fetch-on-mount' : 'prefer-cache'
  });

  // for share activities
  // currently the activity of (un)resolving a comment is also called `CommentCreated`
  // we use the `comment.resolution` to determine whether it's a resolve action
  // if so, should get the resolved comment to show the message
  const threadRel =
    subjectRID?.type === 'comment' &&
    activitySubject.resource?.relationships?.thread;
  const threadId: string =
    threadRel && getData(activitySubject.resource, 'thread')?.id;

  const thread = useResource__ALPHA({
    resourceId: threadId,
    resourceType: threadId ? 'comment' : undefined
  });

  /** If an asset is trashed, this will be undefined */
  const node = activitySubject.resource as ContentTreeNode;

  const isDeleted = node?.deleted_at;

  const nodeResource = useResource__ALPHA({
    resourceId: !isDeleted ? getData(node, 'resource')?.id : undefined,
    resourceType: getData(node, 'resource')?.type
  });

  /** If an asset is trashed, we will use the `node.display_name` as the subject name */
  const trashedNode = useResource__ALPHA({
    resourceId: subjectRID?.id,
    resourceType:
      isNode || subjectRID?.type === 'asset' ? 'content-tree-node' : undefined
  });

  const loading = activitySubject.loading || nodeResource.loading;
  const status = mergeStatuses(
    [activitySubject.status, nodeResource.status],
    true
  );

  const name = isNode
    ? nodeResource.resource?.display_name
    : activitySubject.resource?.display_name ||
      activitySubject.resource?.full_name ||
      activitySubject.resource?.message;

  const isAsset = isNode
    ? node?.relationships.resource?.type === 'asset'
    : subjectRID.type === 'asset';

  const [nodePathname] = useContextPath({
    resourceId: isNode
      ? getData(node, 'resource')?.id
      : isShare || isProject
      ? getData(activitySubject.resource, 'node')?.id
      : subjectRID?.id,
    deleted: !!node?.deleted_at,
    includeResource: true,
    includeDetail: isAsset
  });

  // add extra checks for share activity
  // open settings if you are the user that created the share
  // otherwise go to detailview
  let pathname = nodePathname;
  const [currentUserId] = useCurrentUser(true);
  const [workspaceId] = useCurrentWorkspace();
  if (isShare && currentUserId === actor) {
    pathname = generatePath(urlStructureShareRedirectSettings, {
      shareId: subjectRID?.id
    });
  }
  // if it's contract activities, open the contract view
  if (isContract && activitySubject) {
    pathname = generatePath(urlStructureContractView, {
      workspaceId,
      contractId: subjectRID?.id
    });
  }
  const fallbackName =
    subjectRID?.type &&
    `A${subjectRID.type[0].toLowerCase() === 'a' ? 'n' : ''} ${
      subjectRID.type
    }`;
  return {
    thread: thread.resource,
    name: name || trashedNode.resource?.display_name || fallbackName || '',
    resource: nodeResource.resource || activitySubject.resource,
    pathname: pathname,
    loading,
    status,
    shareIntent:
      activitySubject.resource?.type === 'share' &&
      activitySubject.resource?.['intent']
  };
};
